import {
  GroopitFilterDisplayDTO,
  NamedGroopitFilterDisplayDTO,
} from "groopit-js/client";
import React, { ReactNode, useEffect, useReducer, useState } from "react";
import { FilterReducer } from "./FilterReducer";
import {
  isDefaultFilter,
  UnnamedGroopitFilterDisplayDTO,
} from "../GroopitFilterDTO";
import { FilterContext, FilterModes } from "./FilterContext";
import { SavedFiltersCollectionReducer } from "./SavedFiltersReducer";
import { useSessionStorage } from "react-use";
import { createFilterReplaceFilterAction } from "./FilterActions";
import { FilterDispatchContext } from "./FilterDispatchContext";

interface FilterProviderProps {
  groopId: string;
  lastUsedFilterPrefix: string;
  filter?: GroopitFilterDisplayDTO;
  children: ReactNode;
}

export const FilterProvider = (props: FilterProviderProps): JSX.Element => {
  const { groopId, lastUsedFilterPrefix, children } = props;

  const [filter, filterDispatcher] = useReducer(
    FilterReducer,
    props.filter
      ? new UnnamedGroopitFilterDisplayDTO({ filter: props.filter })
      : new UnnamedGroopitFilterDisplayDTO(),
  );
  const [filterMode, setFilterMode] = useState<FilterModes>("saved");
  const [savedFilters, savedFiltersDispatcher] = useReducer(
    SavedFiltersCollectionReducer,
    undefined,
  );

  // DTO objects generated by the API override the toJSON method but there's
  // no way to override the deserialization method and `useSessionStorage`
  // doesn't provide an option. Instead of playing a bunch of shenanigans to
  // covert things to the right format, we'll just store the raw string in
  // session storage and then have a `useEffect` that parses it.
  const [lastUsedFilter, setLastUsedFilter] =
    useState<GroopitFilterDisplayDTO>();
  const [lastUsedFilterRaw, setLastUsedFilterRaw] = useSessionStorage<string>(
    `${lastUsedFilterPrefix}_${groopId}`,
    undefined,
    true,
  );
  useEffect(() => {
    // useSessionStorage really doesn't like undefined values.
    if (lastUsedFilterRaw && lastUsedFilterRaw !== "undefined") {
      setLastUsedFilter(
        GroopitFilterDisplayDTO.fromJS(JSON.parse(lastUsedFilterRaw)),
      );
    }
  }, [lastUsedFilterRaw]);

  useEffect(() => {
    // Keep track of the last filter the user used. This is poor man's save state
    // feature. It helps with scenarios like filtering down the feed, then editing
    // and/or deleting a Groopit. When you return to the feed your filters are
    // cleared, but we have a button to re-apply the last used filters. It's also
    // helpful if you're generating the same report over and over again.
    //
    // The complicated part here is that you can set the filter through query parameters
    // which means the filter might be non-null and not _really_ be a filter because the
    // caller has basically sent in empty options. We could make it nullable, but that
    // requires everybody else to be aware of what a null filter means. Instead, it's
    // much simpler to say the filter is non-null and only have this code worry about
    // whether it should be saved or not.
    if (!isDefaultFilter(filter)) {
      setLastUsedFilterRaw(
        JSON.stringify(new GroopitFilterDisplayDTO(filter.filter).toJSON()),
      );
    }
  }, [filter, setLastUsedFilterRaw]);

  useEffect(() => {
    // If we've got a Named Filter selected and it's not in the list of saved filters
    // then it must have been deleted. Replace the current filter with an unnamed one
    if (filter instanceof NamedGroopitFilterDisplayDTO) {
      if (!savedFilters?.find((f) => f.id === filter.id)) {
        filterDispatcher(
          createFilterReplaceFilterAction(
            new UnnamedGroopitFilterDisplayDTO({ filter: filter.filter }),
          ),
        );
      }
    }
  }, [filter, savedFilters]);

  return (
    <FilterContext.Provider
      value={{
        filter,
        lastUsedFilter,
        filterMode,
        savedFilters,
      }}
    >
      <FilterDispatchContext.Provider
        value={{
          filterDispatcher,
          setFilterMode,
          savedFiltersDispatcher,
        }}
      >
        {children}
      </FilterDispatchContext.Provider>
    </FilterContext.Provider>
  );
};
